export interface OrderItemDate {
  name: string;
  status: string;
  type: string;
  date: Date | undefined;
  endDate: Date | undefined;
}

/**
 * Extracts all order items with their dates into a consistent format.
 * This includes accommodation, bedbank, experiences, flights, insurance,
 * tours, cruises, car hire, and custom offers.
 */
export function getOrderItemDates(order: App.Order): Array<OrderItemDate> {
  const orderItemsAggregate: Array<OrderItemDate> = [];

  if (order.has_bedbank) {
    order.bedbank_items.forEach((bedbankItem) => {
      orderItemsAggregate.push({
        name: bedbankItem.name,
        type: bedbankItem.type,
        status: bedbankItem.status,
        date: new Date(bedbankItem.check_in),
        endDate: bedbankItem.check_out ? new Date(bedbankItem.check_out) : undefined,
      });
    });
  }

  if (order.accommodation_items.length > 0) {
    order.accommodation_items.forEach((accomodationItem) => {
      const endDateStr = accomodationItem.reservation?.check_out || accomodationItem.reservation?.end_date;
      orderItemsAggregate.push({
        name: accomodationItem.name,
        status: accomodationItem.status,
        type: accomodationItem.type,
        date: new Date(accomodationItem.reservation?.check_in || accomodationItem.reservation?.start_date),
        endDate: endDateStr ? new Date(endDateStr) : undefined,
      });
    });
  }

  if (order.has_experience) {
    order.experience_items
      .filter((exp) => !exp.complimentary)
      .forEach((exp) => {
        orderItemsAggregate.push({
          name: exp.title,
          type: exp.type,
          status: exp.status,
          date: exp?.ticket?.date ? new Date(exp.ticket.date) : undefined,
          endDate: undefined,
        });
      });
  }

  if (order.has_flight) {
    order.flight_items.forEach((flightItem) => {
      // TODO: export appropriate types from backend service and use it here
      if (flightItem.journey?.departing?.flights?.length) {
        // Get return flight date if exists
        const returnDate = flightItem.journey?.returning?.flights?.length
          ? new Date(
              Math.max(
                ...flightItem.journey.returning.flights.map((flight) => new Date(flight.departing_date).getTime()),
              ),
            )
          : undefined;

        orderItemsAggregate.push({
          name: '',
          type: flightItem.type,
          status: flightItem.status,
          // there could be multiple flights, hence select the earliest one.
          date: new Date(
            Math.min(
              ...flightItem.journey.departing.flights.map((flight) => new Date(flight.departing_date).getTime()),
            ),
          ),
          endDate: returnDate,
        });
      }
    });
  }

  if (order.has_insurance) {
    order.insurance_items.forEach((insurance) => {
      orderItemsAggregate.push({
        name: '',
        type: insurance.type,
        status: insurance.status,
        date: insurance.start_date ? new Date(insurance.start_date) : undefined,
        endDate: insurance.end_date ? new Date(insurance.end_date) : undefined,
      });
    });
  }

  if (order.has_tour) {
    order.tour_items.forEach((tour) => {
      orderItemsAggregate.push({
        name: tour.tour_name,
        type: tour.type,
        status: tour.status,
        date: tour.start_date ? new Date(tour.start_date) : undefined,
        endDate: tour.end_date ? new Date(tour.end_date) : undefined,
      });
    });
  }

  if (order.cruise_items.length > 0) {
    order.cruise_items.forEach((cruiseItem) => {
      orderItemsAggregate.push({
        name: cruiseItem.offer_name,
        type: cruiseItem.type,
        status: cruiseItem.status,
        date: cruiseItem.departure_date ? new Date(cruiseItem.departure_date) : undefined,
        endDate: undefined,
      });
    });
  }

  if (order.car_hire_items.length > 0) {
    order.car_hire_items.forEach((carHire) => {
      const returnDate = (carHire.reservation?.return as any)?.date;
      orderItemsAggregate.push({
        name: '',
        type: carHire.type,
        status: carHire.status,
        date: new Date(carHire.reservation?.pickUp?.date),
        endDate: returnDate ? new Date(returnDate) : undefined,
      });
    });
  }

  if (order.custom_offer_items.length > 0) {
    order.custom_offer_items.forEach((customOfferItem) => {
      const customItem = customOfferItem.custom_offer?.items?.[0];
      if (customItem) {
        orderItemsAggregate.push({
          name: customItem.name,
          type: customItem.type,
          status: customOfferItem.status || 'pending',
          date: customItem.start_date ? new Date(customItem.start_date) : undefined,
          endDate: customItem.end_date ? new Date(customItem.end_date) : undefined,
        });
      }
    });
  }

  return orderItemsAggregate;
}
